// src/pages/Platform/switchPushRules.ts — the decisions behind "Push to switch" and
// "Retire number" on Number Ownership, kept out of the component so they can be tested.
import type { ProviderNumber } from 'api/services/providers.service';
import type {
  SwitchPushMode, SwitchPushPreview, SwitchPushPlan, SwitchPushRecord, SwitchPushAction, SwitchRow,
} from 'api/services/switchPush.service';

/** Both spellings of the platform role: the enum is mid-rename and real users hold the legacy one. */
export const isPlatformAdmin = (role: string | null | undefined): boolean =>
  ['master_admin', 'platform_admin'].includes((role || '').toLowerCase());

export const isRetired = (n: Partial<ProviderNumber> | null | undefined): boolean => n?.status === 'RETIRED';

/** Stated wherever parking is mentioned, so nobody reads "parked" as "silent" or "free of charge". */
export const PARKED_NOTE =
  'Parked numbers are still answered by the Free flow; they are billed to Teeoff (internal), never a customer.';

/** A plan the server would write on apply. */
export const writes = (p: Pick<SwitchPushPlan, 'action'>): boolean =>
  p.action === 'ASSIGN' || p.action === 'PARK' || p.action === 'CREATE';

/**
 * Why "Apply" is not available, or null when it is. Mode first: in preview mode the
 * server refuses every apply, so even a fully applicable plan cannot be written.
 */
export const applyBlockReason = (mode: SwitchPushMode | null | undefined,
                                 preview: SwitchPushPreview | null | undefined): string | null => {
  if (!preview) return 'Preview first.';
  const m = mode || preview.mode;
  if (m === 'OFF') return 'Switch push is off on the server (cpass.numbers.switch-push.mode=off).';
  if (m !== 'APPLY') {
    return 'Preview mode: the server shows the plan but refuses to write it (cpass.numbers.switch-push.mode=preview).';
  }
  if (!preview.plans.some(writes)) return 'Nothing to apply: every number is unchanged or blocked.';
  return null;
};

export const applicableCount = (preview: SwitchPushPreview | null | undefined): number =>
  (preview?.plans || []).filter(writes).length;

/** "CMC_B2B · flow cmc_b2b", or a dash. */
export const describeRow = (r: SwitchRow | null | undefined): string => {
  if (!r) return '—';
  const label = r.businessId || '(no label)';
  return r.serviceId ? `${label} · flow ${r.serviceId}` : label;
};

/** The row the plan is about: its target, else the single home row, else nothing. */
export const currentRow = (p: SwitchPushPlan): SwitchRow | null =>
  p.target || (p.homeRows && p.homeRows.length === 1 ? p.homeRows[0] : null);

export const ACTION_TEXT: Record<SwitchPushAction, string> = {
  ASSIGN: 'Relabel',
  PARK: 'Park as FREE',
  NO_CHANGE: 'No change',
  BLOCKED: 'Blocked',
  ROLLBACK: 'Rollback',
  CREATE: 'Create row',
  SETTINGS: 'Settings',
};

export const actionPill = (a: SwitchPushAction): string => {
  switch (a) {
    case 'ASSIGN': return 'tl-pill tl-pill-success';
    case 'CREATE': return 'tl-pill tl-pill-success';
    case 'PARK': return 'tl-pill tl-pill-amber';
    case 'BLOCKED': return 'tl-pill tl-pill-coral';
    default: return 'tl-pill';
  }
};

/**
 * Whether a history row can still be rolled back: a successful ASSIGN or PARK that no
 * successful ROLLBACK has undone yet. The server enforces the same; this hides a button
 * that would only be refused.
 */
export const canRollback = (rec: SwitchPushRecord, all: SwitchPushRecord[]): boolean =>
  rec.status === 'OK'
  && (rec.action === 'ASSIGN' || rec.action === 'PARK' || rec.action === 'CREATE' || rec.action === 'SETTINGS')
  && !all.some(r => r.action === 'ROLLBACK' && r.rollbackOf === rec.id && r.status === 'OK');

/** What retiring does, spelled out in the confirmation. Depends on the switch-push mode. */
export const retireConsequences = (numbers: ProviderNumber[], mode: SwitchPushMode | null | undefined): string[] => {
  const held = numbers.filter(n => !!n.purchased_by_organization_id).length;
  const out = [
    held > 0
      ? `The open tenure is closed for ${held} held number${held === 1 ? '' : 's'}: billing stops attributing calls to the holder from now on, and the holder can no longer present it as caller ID.`
      : 'None of these is held, so no tenure is closed.',
    'The number is marked RETIRED and can never be allocated until it is unretired. Nothing is deleted — its history stays.',
    'Its phone_numbers routing row (if any) is marked inactive.',
  ];
  if (mode === 'APPLY') {
    out.push('Switch push is ON: each number is then parked on its home switch as FREE (audited, and can be rolled back).');
  } else if (mode === 'PREVIEW') {
    out.push('Switch push is in preview mode: the switch row is NOT changed; you will see what parking it would do.');
  } else {
    out.push('Switch push is off: the switch row is not changed.');
  }
  return out;
};

/** Toggle one id in a selection, keeping order stable. */
export const toggleId = (sel: number[], id: number): number[] =>
  sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id];
